/* File: frontend/src/components/reputation/ReputationProfileEditor.js */
/* Version: v12.0 (Global UI Unification) */
/* Design System: Clean, Tech, High Density */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Zap, BarChart3, Shield, X, FileText, MessageSquare, Save,
    RefreshCw, Wand2, MapPin, Upload, CheckCircle, Check, Info, FileText as FileIcon,
    Cpu, Sparkles, AlertCircle, Eye, Clock, Send, Edit3, ImageOff,
    Linkedin, Facebook, Instagram, Twitter, Youtube, Globe,
    History as HistoryIcon, Swords, LayoutGrid, Fingerprint, Image, Share2
} from 'lucide-react';

// --- IMPORTS ---
import AssetHistoryList from './AssetHistoryList';
import ContentWarfarePanel from './ContentWarfarePanel';
import ContentStrategyPanel from './ContentStrategyPanel'; // Kept if needed by other components, but logic mainly here
import AgentBattleInterface from './AgentBattleInterface';
import BattleAnalysisView from './BattleAnalysisView';
import AnalyticsView from './AnalyticsView';
import SmartReputationFeed from '../dashboard/reputation/SmartReputationFeed';

import reputationService from '../../services/reputationService';
import apiService from '../../services/apiService';

import { useAuth } from '../../context/AuthContext'; // [SEC] Import Auth

// --- GLOBAL STYLES (Injected) ---
const GlobalStyles = () => (
    <style>{`
        /* SCROLLBARS (Global Standard) */
        .ui-scroll::-webkit-scrollbar,
        .modal-body-scroll::-webkit-scrollbar { width: 6px; }
        .ui-scroll::-webkit-scrollbar-track,
        .modal-body-scroll::-webkit-scrollbar-track { background: transparent; }
        .ui-scroll::-webkit-scrollbar-thumb,
        .modal-body-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        .ui-scroll::-webkit-scrollbar-thumb:hover,
        .modal-body-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

        /* INPUTS (Chat Style) */
        .ui-input {
            width: 100%;
            background-color: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            padding: 8px 10px;
            font-size: 12px;
            color: #0f172a;
            transition: all 0.2s;
            outline: none;
        }
        .ui-input:focus {
            background-color: #fff;
            border-color: #3b82f6;
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }
        .ui-label {
            display: block;
            font-size: 11px;
            font-weight: 600;
            color: #64748b;
            margin-bottom: 4px;
            text-transform: uppercase;
            letter-spacing: 0.02em;
        }

        /* CARDS (Unified) */
        .ui-card {
            background: #fff;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 16px;
            box-shadow: 0 1px 2px rgba(0,0,0,0.05);
            display: flex; flex-direction: column; gap: 12px;
        }
        .ui-card-header {
            display: flex; align-items: center; gap: 8px;
            font-size: 13px; font-weight: 700; color: #1e293b;
            padding-bottom: 12px; border-bottom: 1px solid #f1f5f9;
            margin-bottom: 4px;
        }

        /* TABS (Sticky & Clean) */
        .ui-tabs-strip {
            display: flex; gap: 4px; padding: 0 20px;
            border-bottom: 1px solid #e2e8f0;
            background: #fff;
            position: sticky; top: 0; z-index: 20;
        }
        .ui-tab-btn {
            display: flex; align-items: center; gap: 6px;
            padding: 12px 14px;
            font-size: 12px; font-weight: 600;
            color: #64748b;
            background: transparent; border: none;
            cursor: pointer;
            border-bottom: 2px solid transparent;
            transition: all 0.2s;
        }
        .ui-tab-btn:hover { color: #334155; background: #f8fafc; }
        .ui-tab-btn.active {
            color: #2563eb;
            background: #eff6ff;
            border-bottom-color: #2563eb;
        }
    `}</style>
);

// --- UTILS ---
const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const len = phoneNumber.length;
    if (len < 4) return phoneNumber;
    if (len < 7) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const Toast = ({ message, type, onClose }) => (
    <div className={`toast-notification ${type}`} style={{
        position: 'absolute', top: '20px', right: '20px', zIndex: 11000,
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '10px 16px', borderRadius: '8px',
        background: type === 'error' ? '#fef2f2' : '#f0fdf4',
        border: `1px solid ${type === 'error' ? '#fecaca' : '#bbf7d0'}`,
        color: type === 'error' ? '#ef4444' : '#16a34a',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        minWidth: '300px', maxWidth: '400px'
    }}>
        {type === 'success' && <CheckCircle size={18} />}
        {type === 'error' && <AlertCircle size={18} />}
        {type === 'info' && <Info size={18} />}
        <span style={{ flex: 1, fontSize: '13px', fontWeight: '500' }}>{message}</span>
        <button onClick={onClose} style={{ background: 'transparent', border: 'None', cursor: 'pointer', color: 'inherit', display: 'flex' }}><X size={14} /></button>
    </div>
);

// --- IMAGE COMPONENTS ---
const resolveImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('blob:') || url.startsWith('data:')) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.includes('uploads/')) {
        const parts = url.split('uploads/');
        const filename = parts[parts.length - 1].replace(/^\//, '');
        return `/api/v1/uploads/${filename}`;
    }
    if (url.startsWith('/api/v1/uploads/')) return url;
    if (url.startsWith('/')) return url;
    return `/api/v1/uploads/${url}`;
};

const ImageThumbnail = ({ src, type = 'cover' }) => {
    const [hasError, setHasError] = useState(false);
    const finalSrc = resolveImageUrl(src);

    useEffect(() => {
        setHasError(false);
    }, [src]);

    if (!finalSrc || hasError) return (
        <div style={{ width: 48, height: 48, borderRadius: 6, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0', flexShrink: 0 }}>
            <ImageOff size={16} color="#cbd5e1" />
        </div>
    );
    return (
        <div style={{ width: 48, height: 48, borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#fff', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img
                src={finalSrc}
                alt="Preview"
                style={{ width: '100%', height: '100%', objectFit: type }}
                onError={() => setHasError(true)}
            />
        </div>
    );
};

// --- MAIN COMPONENT ---
const ReputationProfileEditor = ({ profile, onClose, onSave, viewMode = 'normal', initialTab = 'truth' }) => {
    // STATE
    const { user } = useAuth(); // [SEC] Get User
    const [activeTab, setActiveTab] = useState(initialTab);
    const [notification, setNotification] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [previews, setPreviews] = useState({});

    // [SEC] Admin Check
    // FIX: roles is a list of objects [{name: 'ADMIN'}, ...], not a single string.
    const isAdmin = Array.isArray(user?.roles)
        ? user.roles.some(r => ['ADMIN', 'ADMINISTRATOR'].includes(r.name?.toUpperCase()))
        : (['admin', 'administrator'].includes(user?.role?.toLowerCase()));

    // [SEC] Redirect unauthorized access to restricted tabs
    useEffect(() => {
        if (!isAdmin && (activeTab === 'warfare' || activeTab === 'feed')) {
            setActiveTab('truth');
        }
    }, [activeTab, isAdmin]);

    // SUB-COMPONENT STATES (Hoisted)
    const [sessions, setSessions] = useState([]);
    const [sessionsError, setSessionsError] = useState(null); // surface backend failures (was silently swallowed)
    const sessionsErrorRef = useRef(false); // suppress repeated toasts on each 5s poll
    const [activeSessionId, setActiveSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputMsg, setInputMsg] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [isAutoMode, setIsAutoMode] = useState(false);
    const [savedFixes, setSavedFixes] = useState([]); // For Warfare
    const [inspectingBattle, setInspectingBattle] = useState(null); // For Battle Analysis Overlay

    // FORM DATA
    const defaultProfile = {
        company_name: '', website_url: '', address: '', location_name: '',
        attorney_name: '', company_description: "", attorney_bio: "",
        gmb_url: '', target_keywords: '', logo_url: '', attorney_photo_url: '',
        contact_phone: '', contact_email: '',
        social_linkedin: '', social_facebook: '', social_instagram: '', social_twitter: '', social_youtube: '',
        auto_battle_enabled: false, battle_frequency: '24h',
        ai_provider: 'openai', openai_api_key: '', gemini_api_key: '', grok_api_key: '', deepseek_api_key: '',
        custom_prompt: '', ai_daily_limit: 3, ai_posting_speed: 'standard', auto_poster_enabled: false,
        active_workers: { gemini: true, gpt: false, grok: false, deepseek: false }
    };

    const [formData, setFormData] = useState(defaultProfile);
    // [FIX] Smart Save: Track initial state
    const [initialData, setInitialData] = useState(defaultProfile);

    // --- UTILS ---
    const showToast = (message, type = 'info') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const fetchSavedFixes = useCallback(async (pId) => {
        if (!pId) return;
        try {
            const fixes = await reputationService.listProfileFixes(pId);
            setSavedFixes(Array.isArray(fixes) ? fixes.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : []);
        } catch (e) {
            console.error('[ReputationEditor] Failed to load saved fixes:', e);
        }
    }, []);

    // --- EFFECTS ---
    useEffect(() => {
        let isMounted = true;
        const loadData = async () => {
            if (profile?.id) {
                try {
                    const fresh = await reputationService.getProfile(profile.id);
                    if (isMounted && fresh) {
                        const mappedData = {
                            ...defaultProfile, ...fresh,
                            website_url: fresh.company_url || fresh.website_url || '',
                            auto_battle_enabled: !!fresh.auto_battle_enabled,
                            auto_poster_enabled: !!fresh.auto_poster_enabled,
                            location_name: fresh.location_name || (fresh.source_of_truth && fresh.source_of_truth.location_name) || '',
                            contact_phone: formatPhoneNumber(fresh.contact_phone),
                            attorney_bio: fresh.attorney_bio || '',
                            target_keywords: Array.isArray(fresh.target_keywords) ? fresh.target_keywords.join(', ') : (fresh.target_keywords || ''),
                            active_workers: (fresh.source_of_truth?.active_workers) || { gemini: true, gpt: false, grok: false, deepseek: false },
                            grok_api_key: fresh.grok_api_key || '',
                            deepseek_api_key: fresh.deepseek_api_key || ''
                        };
                        setFormData(mappedData);
                        setInitialData(mappedData); // [FIX] Sync Baseline
                        fetchSavedFixes(fresh.id);
                    }
                } catch (e) {
                    const fallback = {
                        ...defaultProfile, ...profile,
                        target_keywords: Array.isArray(profile.target_keywords) ? profile.target_keywords.join(', ') : (profile.target_keywords || '')
                    };
                    setFormData(fallback);
                    setInitialData(fallback);
                }
            } else {
                setFormData(defaultProfile);
                setInitialData(defaultProfile);
            }
        };
        loadData();
        return () => { isMounted = false; };
    }, [profile, fetchSavedFixes]);

    // Memoize locationQuery so it doesn't churn on every keystroke in formData
    const locationQuery = useMemo(() => {
        if (formData.address?.includes(',')) return formData.address.split(',')[1].trim();
        if (formData.company_name?.includes('(')) return formData.company_name.match(/\(([^)]+)\)/)?.[1] || null;
        return null;
    }, [formData.address, formData.company_name]);

    // Polling (Chat & Feed)
    useEffect(() => {
        if (activeTab !== 'chat' && activeTab !== 'feed') return;
        let isMounted = true;
        const fetchSessions = async () => {
            // Skip polling while tab is hidden — saves backend round-trips
            if (typeof document !== 'undefined' && document.hidden) return;
            try {
                const data = await reputationService.getBattleSessions(profile?.id, locationQuery);
                if (!isMounted) return;
                setSessions(data);
                if (sessionsErrorRef.current) {
                    sessionsErrorRef.current = false;
                    setSessionsError(null);
                    showToast('Mission log restored.', 'success');
                }
            } catch (e) {
                if (!isMounted) return;
                console.error('[ReputationEditor] getBattleSessions failed:', e);
                setSessionsError(e?.message || 'Cannot load missions');
                if (!sessionsErrorRef.current) {
                    sessionsErrorRef.current = true;
                    showToast('Cannot load missions — retrying every 5s', 'error');
                }
            }
        };
        fetchSessions();
        const interval = setInterval(fetchSessions, 5000);
        return () => { isMounted = false; clearInterval(interval); };
    }, [activeTab, profile?.id, locationQuery]);

    // Message Sync
    useEffect(() => {
        if (activeSessionId && sessions.length > 0) {
            const s = sessions.find(sess => (sess.id === activeSessionId || sess.session_id === activeSessionId));
            if (s?.messages) setMessages(s.messages);
        }
    }, [activeSessionId, sessions]);

    // --- HANDLERS ---
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const handleFileUpload = async (e, fieldName) => {
        const file = e.target.files[0];
        if (!file) return;
        const localBlob = URL.createObjectURL(file);
        setPreviews(prev => ({ ...prev, [fieldName]: localBlob }));
        setUploading(true);
        try {
            const res = await reputationService.uploadMedia(file);
            const uploadedPath = res.file_path;
            setFormData(prev => ({ ...prev, [fieldName]: uploadedPath }));
            showToast("Uploaded successfully!", 'success');
        } catch (e) {
            console.error("Upload error:", e);
            showToast("Upload Failed: " + (e?.response?.data?.detail || e.message), 'error');
        }
        finally { setUploading(false); }
    };

    const handleSmartScan = async () => {
        if (!formData.website_url) return showToast("Enter URL first", 'error');
        setScanning(true);
        try {
            const data = await apiService.makeRequest('/reputation/profiles/smart-scan', {
                method: 'POST', body: JSON.stringify({ url: formData.website_url }),
            });
            if (data.error) throw new Error(data.error);

            setFormData(prev => ({
                ...prev, ...data,
                location_name: data.address?.split(',')?.[1]?.trim() || prev.location_name || ""
            }));
            showToast("Scan Complete!", 'success');
        } catch (e) { showToast("Scan Error: " + e.message, 'error'); }
        finally { setScanning(false); }
    };

    const handleSubmit = async () => {
        // [FIX] Pack active_workers into source_of_truth to ensure backend persistence
        const payload = {
            ...formData,
            company_url: formData.website_url,
            source_of_truth: {
                ...(formData.source_of_truth || {}),
                active_workers: formData.active_workers
            }
        };

        try {
            let saved = null;
            if (profile?.id && profile.id !== 'main' && profile.id !== 'hallandale') {
                saved = await reputationService.updateProfile(profile.id, payload);
            } else {
                saved = await reputationService.createProfile(payload);
            }
            if (saved) {
                const mapped = {
                    ...defaultProfile, ...saved,
                    website_url: saved.company_url || saved.website_url || formData.website_url,
                    target_keywords: Array.isArray(saved.target_keywords) ? saved.target_keywords.join(', ') : (saved.target_keywords || formData.target_keywords)
                };
                setFormData(mapped);
                setInitialData(mapped);
            } else {
                setInitialData({ ...formData });
            }
            showToast("Configuration Saved!", 'success');
            if (onSave) onSave(profile?.id, saved || payload);
        } catch (e) {
            console.error("Save failed", e);
            showToast("Save Failed: " + (e?.response?.data?.detail || e.message), 'error');
        }
    };

    // --- CHAT LOGIC ---
    const startNewBattle = async (opts = {}) => {
        if (!profile?.id) return showToast("Save profile first", 'error');
        setIsTyping(true);

        // [FIX] Source of Truth Integration: Keywords
        let selectedKeyword = "Lawyer"; // Fallback
        if (formData.target_keywords && formData.target_keywords.trim()) {
            const keywords = formData.target_keywords.split(',').map(k => k.trim()).filter(k => k.length > 0);
            if (keywords.length > 0) {
                // Pick random or first? Random ensures coverage.
                selectedKeyword = keywords[Math.floor(Math.random() * keywords.length)];
            }
        }

        const tempId = Date.now().toString();
        // Use selected keyword in title preview
        setSessions(prev => [{ id: tempId, title: `🚀 Initializing: ${selectedKeyword}...`, active: true }, ...prev]);
        setActiveSessionId(tempId);
        setMessages([]);
        try {
            const data = await reputationService.startBattle({
                profile_id: profile.id,
                keyword: selectedKeyword,
                worker_count: opts.worker_count || 1,
                // [FIX] Pass Active Workers config for Quad-Core (Support Override)
                active_workers: opts.active_workers || formData.active_workers,
                monitor_mode: "quad_core",
                // Pass chosen key for backend to know (if needed, though backend mostly uses keyword arg)
                squad_ids: opts.squad_ids
            });
            setSessions(prev => prev.map(s => s.id === tempId ? { ...s, id: data.session_id, title: data.title } : s));
            setActiveSessionId(data.session_id);
            setMessages(data.messages);
        } catch (e) { showToast("Battle Start Failed", 'error'); setIsTyping(false); }
        finally { setIsTyping(false); }
    };

    const sendReply = async (txt = inputMsg) => {
        if (!txt.trim()) return;
        setInputMsg('');
        setMessages(prev => [...prev, { role: 'user', content: txt }]);
        setIsTyping(true);
        try {
            const data = await reputationService.sendBattleReply(activeSessionId, txt);
            setMessages(data.messages);
        } catch (e) { setMessages(prev => [...prev, { role: 'assistant', content: "Error." }]); }
        finally { setIsTyping(false); }
    };

    // --- CONTENT WARFARE LOGIC ---
    // 🔴 ОТКАЗ МОНТАЖА И ОТКАЗ СЛУЖБЫ — РАЗНЫЕ СОБЫТИЯ, И ЧЕЛОВЕК ОБЯЗАН ИХ
    //    РАЗЛИЧАТЬ. Раньше оба приходили одной строкой «Generation Failed: …»:
    //    `reputationService.generateFix` в сервисе НЕТ (там `generateFixes`, во
    //    множественном числе и с двумя позиционными доводами вместо объекта),
    //    вызов падал на TypeError, общий `catch` ловил его и выдавал за ответ
    //    службы. Невозможность проходила как поведение.
    //
    // 🔴 ЭТО НЕ ЧИНИТ КНОПКУ и не должно. Смонтировать её значит включить путь,
    //    который выдаёт человеку разметку с инструкцией «Paste into <head>», а в
    //    ней `priceRange: "$$$"` — утверждение о ценах фирмы, не взятое из
    //    канона. Монтаж согласуется отдельно (ПРАВИЛО НОЛЬ §2).
    //
    // 🔴 ПРОВЕРКА ИДЁТ ДО ЛЮБОГО УВЕДОМЛЕНИЯ: обещание «Generating Content…»,
    //    показанное перед проверкой, — сообщение о начале того, что не началось.
    const handleGenerateFix = async (fixType, promptOverride = null) => {
        if (!profile?.id) return showToast("No Profile ID", 'error');

        if (typeof reputationService.generateFix !== 'function') {
            console.error(
                '[ReputationProfileEditor] reputationService.generateFix не смонтирован; ' +
                'в сервисе есть generateFixes с другой сигнатурой — переименование не починит вызов'
            );
            return showToast(
                "Ошибка монтажа: метод генерации не подключён в клиенте. Это дефект сборки, а не отказ службы.",
                'error'
            );
        }

        showToast("Generating Content...", 'info');
        try {
            const res = await reputationService.generateFix({
                profile_id: profile.id,
                fix_type: fixType,
                prompt_override: promptOverride || formData.custom_prompt,
                model_provider: formData.ai_provider
            });
            setSavedFixes(prev => [res, ...prev]);
            showToast("Content Generated!", 'success');
        } catch (e) {
            // Связывание могло сломаться и ВНУТРИ вызова — тогда это снова не
            // служба. Различаем по роду ошибки, а не по тексту сообщения.
            if (e instanceof TypeError) {
                console.error('[ReputationProfileEditor] дефект связывания при вызове:', e);
                return showToast(
                    "Ошибка монтажа при вызове: " + e.message + ". Это дефект сборки, а не отказ службы.",
                    'error'
                );
            }
            showToast("Generation Failed (Service Error): " + e.message, 'error');
        }
    };

    const handleGenerateSchema = () => handleGenerateFix('schema_json_ld');

    const handlePostToWP = async (fix) => {
        if (!fix || !fix.id) return;
        try {
            await reputationService.postToWordpress(fix.id);
            showToast("Posted to WordPress!", 'success');
        } catch (e) { showToast("WP Post Failed: " + e.message, 'error'); }
    };

    // --- FEED LOGIC ---
    const handleFixFromFeed = (data) => {
        setActiveTab('warfare');
        setFormData(prev => ({ ...prev, custom_prompt: `Focus on keyword: ${data.keyword} in ${data.location}` }));
        showToast("Redirecting to Warfare...", 'info');
    };

    // --- PREVIEW LOGIC ---
    // Minimal setter for now, can be expanded to full modal later
    const setPreviewContent = (content) => {
        // Logic to open a preview modal could go here.
        // For now, we just copy to clipboard as a fallback fallback or simple alert if needed, 
        // but the prop implies a UI action. Let's just log or minimal toast for now to satisfy the prop.
        // Ideally, we add a state: const [previewModalContent, setPreviewModalContent] = useState(null);
        // But to fix the build error quickly:
        console.log("Preview requested:", content.substring(0, 50) + "...");
        navigator.clipboard.writeText(content);
        showToast("Content copied to clipboard for preview", 'info');
    };

    // --- RENDER HELPERS ---
    const LocationSuffix = formData.location_name ? ` (${formData.location_name})` : '';
    // [FIX] Smart Save Logic: Simple deep compare for "dirty" state
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)',
            zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
            <GlobalStyles />
            {notification && <Toast {...notification} onClose={() => setNotification(null)} />}

            <div style={{
                width: '90%', maxWidth: '1100px', height: '85vh',
                backgroundColor: '#FBFBFA', borderRadius: '20px', border: '1px solid #E5E1D6',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative'
            }}>
                {/* 1. GLOBAL HEADER */}
                <div style={{
                    padding: '16px 24px', backgroundColor: '#fff',
                    borderBottom: '1px solid #E5E1D6', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {activeTab === 'truth' && <FileText size={20} className="text-[#C8A653]" />}
                        {activeTab === 'chat' && <Zap size={20} className="text-[#C8A653]" />}
                        {activeTab === 'analytics' && <BarChart3 size={20} className="text-[#C8A653]" />}
                        {activeTab === 'warfare' && <Swords size={20} className="text-red-500" />}
                        {activeTab === 'feed' && <HistoryIcon size={20} className="text-[#C8A653]" />}

                        <h2 style={{ fontSize: '17px', fontFamily: 'Georgia, serif', fontWeight: '700', color: '#20231F', margin: 0 }}>
                            {activeTab === 'truth' ? 'Source of Truth' :
                                activeTab === 'chat' ? 'Agent Battle Arena' :
                                    activeTab === 'analytics' ? 'Analytics Command' :
                                        activeTab === 'warfare' ? 'Content Warfare' : 'Smart Reputation Feed'}
                            <span style={{ fontWeight: '400', color: '#64748b' }}>{LocationSuffix}</span>
                        </h2>
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        {/* [FIX] Global Smart Save Button */}
                        <button
                            onClick={hasChanges ? handleSubmit : undefined}
                            className={hasChanges ? "btn-primary" : "btn-neutral"}
                            disabled={!hasChanges}
                            style={{
                                padding: '6px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                                background: hasChanges ? '#20231F' : '#F4F2EC',
                                color: hasChanges ? '#FFFFFF' : '#8A9286',
                                border: 'none',
                                cursor: hasChanges ? 'pointer' : 'default',
                                display: 'flex', alignItems: 'center', gap: '6px',
                                transition: 'all 0.2s',
                                boxShadow: hasChanges ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                            }}>
                            {hasChanges ? <Save size={14} /> : <Check size={14} />}
                            {hasChanges ? 'Save Changes' : 'Saved'}
                        </button>

                        <button onClick={onClose} style={{
                            padding: '7px', borderRadius: '10px', border: '1px solid #E5E1D6', background: '#FFFFFF', cursor: 'pointer', color: '#687077'
                        }}>
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* 2. NAVIGATION (Sticky) */}
                <div className="ui-tabs-strip">
                    <button className={`ui-tab-btn ${activeTab === 'truth' ? 'active' : ''}`} onClick={() => setActiveTab('truth')}><FileText size={14} /> Source of Truth</button>
                    <button className={`ui-tab-btn ${activeTab === 'chat' ? 'active' : ''}`} onClick={() => setActiveTab('chat')}><MessageSquare size={14} /> Agent Battle</button>
                    <button className={`ui-tab-btn ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}><BarChart3 size={14} /> Analytics</button>

                    {/* [SEC] Restricted Tabs */}
                    {isAdmin && (
                        <>
                            <button className={`ui-tab-btn ${activeTab === 'warfare' ? 'active' : ''}`} onClick={() => setActiveTab('warfare')}><Swords size={14} /> Content Warfare</button>
                            <button className={`ui-tab-btn ${activeTab === 'feed' ? 'active' : ''}`} onClick={() => setActiveTab('feed')}><HistoryIcon size={14} /> Smart Feed</button>
                        </>
                    )}
                </div>

                {/* 3. CONTENT AREA (Scroll Jail) */}
                <div className="modal-body-scroll" style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>

                    {/* === TAB: SOURCE OF TRUTH (Refactored Cards) === */}
                    {activeTab === 'truth' && (
                        <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(300px, 2fr) minmax(280px, 1fr)', gap: '20px' }}>

                            {/* LEFT COLUMN */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                                {/* CARD 1: CORE IDENTITY */}
                                <div className="ui-card">
                                    <div className="ui-card-header"><Fingerprint size={16} className="text-[#C8A653]" /> CORE IDENTITY</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                        <div style={{ gridColumn: 'span 2' }}>
                                            <label className="ui-label">Website URL</label>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <input className="ui-input" name="website_url" value={formData.website_url} onChange={handleInputChange} placeholder="https://..." />
                                                <button onClick={handleSmartScan} disabled={scanning} style={{
                                                    whiteSpace: 'nowrap', padding: '0 12px', background: '#FAF4E5', color: '#A97F2E',
                                                    border: '1px solid #E5D7B5', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
                                                }}>
                                                    {scanning ? <RefreshCw size={12} className="spin" /> : <Wand2 size={12} />} {scanning ? 'Scanning' : 'Auto-Fill'}
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="ui-label">Company Name</label>
                                            <input className="ui-input" name="company_name" value={formData.company_name} onChange={handleInputChange} />
                                        </div>
                                        <div>
                                            <label className="ui-label">Location (City)</label>
                                            <input className="ui-input" name="location_name" value={formData.location_name} onChange={handleInputChange} placeholder="e.g. Bronx" />
                                        </div>
                                        <div style={{ gridColumn: 'span 2' }}>
                                            <label className="ui-label">Full Address</label>
                                            <input className="ui-input" name="address" value={formData.address} onChange={handleInputChange} />
                                        </div>
                                        <div>
                                            <label className="ui-label">Attorney Name</label>
                                            <input className="ui-input" name="attorney_name" value={formData.attorney_name} onChange={handleInputChange} />
                                        </div>
                                        <div style={{ gridColumn: 'span 2' }}>
                                            <label className="ui-label">Attorney Bio</label>
                                            <textarea className="ui-input" rows={3} name="attorney_bio" value={formData.attorney_bio} onChange={handleInputChange} placeholder="Brief biography..." style={{ resize: 'vertical' }} />
                                        </div>
                                        <div>
                                            <label className="ui-label">Contact Phone</label>
                                            <input className="ui-input" name="contact_phone" value={formData.contact_phone} onChange={handleInputChange} />
                                        </div>
                                        <div>
                                            <label className="ui-label">Contact Email (Email+)</label>
                                            <input className="ui-input" name="contact_email" value={formData.contact_email} onChange={handleInputChange} placeholder="hello@company.com" />
                                        </div>
                                    </div>
                                </div>

                                {/* CARD 2: SEO & DESCRIPTION */}
                                <div className="ui-card">
                                    <div className="ui-card-header"><FileText size={16} className="text-slate-500" /> SEO & CONTEXT</div>
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <label className="ui-label">Business Description</label>
                                            <button className="btn-xs" style={{ fontSize: '10px', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Sparkles size={10} /> AI Improve
                                            </button>
                                        </div>
                                        <textarea className="ui-input" rows={4} name="company_description" value={formData.company_description} onChange={handleInputChange} style={{ resize: 'vertical' }} />
                                    </div>
                                    <div>
                                        <label className="ui-label">Target Keywords (CSV)</label>
                                        <input className="ui-input" name="target_keywords" value={formData.target_keywords} onChange={handleInputChange} placeholder="estate planning, probate lawyer, etc." />
                                    </div>
                                    <div>
                                        <label className="ui-label">GMB URL</label>
                                        <input className="ui-input" name="gmb_url" value={formData.gmb_url} onChange={handleInputChange} placeholder="https://maps.google.com/..." />
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT COLUMN */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                                {/* CARD 3: MEDIA ASSETS */}
                                <div className="ui-card">
                                    <div className="ui-card-header"><Image size={16} className="text-[#C8A653]" /> BRAND ASSETS</div>
                                    <div style={{ display: 'flex', gap: '16px' }}>
                                        <div style={{ flex: 1 }}>
                                            <label className="ui-label">Attorney Photo</label>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <ImageThumbnail src={previews.attorney_photo_url || formData.attorney_photo_url} />
                                                <label style={{ flex: 1, border: '1px dashed #cbd5e1', borderRadius: '6px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#f8fafc' }}>
                                                    <Upload size={14} className="text-slate-400" />
                                                    <span style={{ fontSize: '10px', color: '#64748b' }}>Upload</span>
                                                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, 'attorney_photo_url')} />
                                                </label>
                                            </div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label className="ui-label">Company Logo</label>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <ImageThumbnail src={previews.logo_url || formData.logo_url} type="contain" />
                                                <label style={{ flex: 1, border: '1px dashed #cbd5e1', borderRadius: '6px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#f8fafc' }}>
                                                    <Upload size={14} className="text-slate-400" />
                                                    <span style={{ fontSize: '10px', color: '#64748b' }}>Upload</span>
                                                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, 'logo_url')} />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* CARD 4: SOCIAL SIGNALS */}
                                <div className="ui-card">
                                    <div className="ui-card-header"><Share2 size={16} className="text-pink-500" /> SOCIAL SIGNALS</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Linkedin size={16} color="#0077b5" />
                                            <input className="ui-input" name="social_linkedin" value={formData.social_linkedin} onChange={handleInputChange} placeholder="LinkedIn" />
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Facebook size={16} color="#1877f2" />
                                            <input className="ui-input" name="social_facebook" value={formData.social_facebook} onChange={handleInputChange} placeholder="Facebook" />
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Instagram size={16} color="#e4405f" />
                                            <input className="ui-input" name="social_instagram" value={formData.social_instagram} onChange={handleInputChange} placeholder="Instagram" />
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Youtube size={16} color="#ff0000" />
                                            <input className="ui-input" name="social_youtube" value={formData.social_youtube} onChange={handleInputChange} placeholder="YouTube Channel" autoComplete="off" />
                                        </div>
                                    </div>
                                </div>

                                {/* CARD 5: AUTOMATION CONTROL */}
                                <div className="ui-card" style={{ border: '1px solid #bfdbfe', background: '#f0f9ff' }}>
                                    <div className="ui-card-header" style={{ color: '#1e40af', borderColor: '#dbeafe', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Cpu size={16} className="text-[#C8A653]" /> AUTOMATION & KEYS</span>
                                        <div
                                            onClick={() => setFormData({ ...formData, auto_battle_enabled: !formData.auto_battle_enabled })}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                                                padding: '4px 8px', borderRadius: '4px', background: formData.auto_battle_enabled ? '#dbeafe' : 'transparent'
                                            }}
                                        >
                                            <span style={{ fontSize: '11px', fontWeight: '600', color: formData.auto_battle_enabled ? '#1e40af' : '#64748b' }}>
                                                {formData.auto_battle_enabled ? 'ENABLED' : 'DISABLED'}
                                            </span>
                                            <div style={{
                                                width: 32, height: 18, borderRadius: 12, position: 'relative', transition: 'all 0.2s',
                                                backgroundColor: formData.auto_battle_enabled ? '#2563eb' : '#cbd5e1'
                                            }}>
                                                <div style={{
                                                    width: 14, height: 14, borderRadius: '50%', backgroundColor: '#fff',
                                                    position: 'absolute', top: 2, left: 2,
                                                    transform: formData.auto_battle_enabled ? 'translateX(14px)' : 'translateX(0)',
                                                    transition: 'all 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                }} />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {/* Workers Checkboxes */}
                                        <div>
                                            <label className="ui-label" style={{ color: '#20231F', marginBottom: '8px', display: 'block' }}>Battle Workers</label>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                {['gemini', 'gpt', 'claude', 'copilot', 'perplexity', 'grok', 'deepseek'].map(worker => {
                                                    const isActive = formData.active_workers?.[worker];
                                                    const label = ({
                                                        gpt: 'GPT-4',
                                                        gemini: 'Gemini',
                                                        claude: 'Claude',
                                                        grok: 'Grok',
                                                        deepseek: 'DeepSeek',
                                                        copilot: 'Copilot',
                                                        perplexity: 'Perplexity',
                                                    })[worker] || worker;
                                                    return (
                                                        <label key={worker} style={{
                                                            display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer',
                                                            backgroundColor: isActive ? '#FAF4E5' : '#FCFBF8',
                                                            border: isActive ? '1px solid #C8A653' : '1px solid #E5E1D6',
                                                            padding: '6px 10px', borderRadius: '6px', transition: 'all 0.2s'
                                                        }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isActive || false}
                                                                onChange={(e) => setFormData(prev => ({
                                                                    ...prev,
                                                                    active_workers: { ...prev.active_workers, [worker]: e.target.checked }
                                                                }))}
                                                                style={{ accentColor: '#C8A653' }}
                                                            />
                                                            <span style={{ fontWeight: isActive ? '600' : '400', color: isActive ? '#20231F' : '#687077' }}>
                                                                {label}
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Claude Model Selector — only when Claude worker is enabled */}
                                        {formData.active_workers?.claude && (
                                            <div>
                                                <label className="ui-label" style={{ color: '#20231F' }}>Claude Model</label>
                                                <select
                                                    className="ui-input"
                                                    name="claude_model"
                                                    value={formData.claude_model || 'claude-haiku-4-5'}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="claude-haiku-4-5">Haiku 4.5 (fast, ~$5/M out)</option>
                                                    <option value="claude-sonnet-4-6">Sonnet 4.6 (smart, ~$15/M out)</option>
                                                </select>
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', gap: '12px' }}>
                                            <div style={{ flex: 1 }}>
                                                <label className="ui-label" style={{ color: '#1e40af' }}>AI Provider</label>
                                                <select className="ui-input" name="ai_provider" value={formData.ai_provider} onChange={handleInputChange}>
                                                    <option value="openai">OpenAI (GPT-4)</option>
                                                    <option value="gemini">Google (Gemini)</option>
                                                </select>
                                            </div>
                                            {/* Daily Limit Control */}
                                            <div style={{ flex: 1 }}>
                                                <label className="ui-label" style={{ color: '#1e40af' }}>Daily Post Limit</label>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <input
                                                        className="ui-input"
                                                        type="number"
                                                        min="1"
                                                        max="100"
                                                        name="ai_daily_limit"
                                                        value={formData.ai_daily_limit}
                                                        onChange={handleInputChange}
                                                        style={{ width: '80px' }}
                                                    />
                                                    <span style={{ fontSize: '11px', color: '#64748b' }}>posts per day</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="ui-label" style={{ color: '#1e40af' }}>OpenAI Key</label>
                                            <input className="ui-input" type="password" name="openai_api_key" value={formData.openai_api_key} onChange={handleInputChange} placeholder={formData.openai_api_key ? "••••••••" : "Default (System Key)"} autoComplete="new-password" />
                                        </div>
                                        <div>
                                            <label className="ui-label" style={{ color: '#1e40af' }}>Gemini Key</label>
                                            <input className="ui-input" type="password" name="gemini_api_key" value={formData.gemini_api_key} onChange={handleInputChange} placeholder={formData.gemini_api_key ? "••••••••" : "Default (System Key)"} autoComplete="new-password" />
                                        </div>
                                        <div>
                                            <label className="ui-label" style={{ color: '#1e40af' }}>Grok Key (xAI)</label>
                                            <input className="ui-input" type="password" name="grok_api_key" value={formData.grok_api_key} onChange={handleInputChange} placeholder={formData.grok_api_key ? "••••••••" : "Default (System Key)"} autoComplete="new-password" />
                                        </div>
                                        <div>
                                            <label className="ui-label" style={{ color: '#1e40af' }}>DeepSeek Key</label>
                                            <input className="ui-input" type="password" name="deepseek_api_key" value={formData.deepseek_api_key} onChange={handleInputChange} placeholder={formData.deepseek_api_key ? "••••••••" : "Default (System Key)"} autoComplete="new-password" />
                                        </div>
                                        <div>
                                            <label className="ui-label" style={{ color: '#1e40af' }}>Claude Key (Anthropic)</label>
                                            <input className="ui-input" type="password" name="anthropic_api_key" value={formData.anthropic_api_key || ''} onChange={handleInputChange} placeholder={formData.anthropic_api_key ? "••••••••" : "Default (System Key)"} autoComplete="new-password" />
                                        </div>
                                        <div>
                                            <label className="ui-label" style={{ color: '#1e40af' }}>Copilot Key (Azure)</label>
                                            <input className="ui-input" type="password" name="copilot_api_key" value={formData.copilot_api_key || ''} onChange={handleInputChange} placeholder={formData.copilot_api_key ? "••••••••" : "Default (System Key)"} autoComplete="new-password" />
                                        </div>
                                        <div>
                                            <label className="ui-label" style={{ color: '#1e40af' }}>Perplexity Key</label>
                                            <input className="ui-input" type="password" name="perplexity_api_key" value={formData.perplexity_api_key || ''} onChange={handleInputChange} placeholder={formData.perplexity_api_key ? "••••••••" : "Default (System Key)"} autoComplete="new-password" />
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}

                    {/* === OTHER TABS (Wrapped in Consistent Shell) === */}
                    {activeTab === 'chat' && (
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <AgentBattleInterface
                                sessions={sessions}
                                activeSessionId={activeSessionId}
                                setActiveSessionId={setActiveSessionId}
                                messages={messages}
                                isTyping={isTyping}
                                inputMsg={inputMsg}
                                setInputMsg={setInputMsg}
                                sendReply={sendReply}
                                startNewBattle={startNewBattle}
                                isAutoMode={isAutoMode}
                                toggleAutoMode={() => setIsAutoMode(!isAutoMode)}
                                isFullScreen={true} // Adapts layout
                                // [NEW] Wired Props
                                profile={profile} // Pass full profile for Auto-Persuasion Check
                                battleFrequency={formData.battle_frequency}
                                onFrequencyChange={(val) => setFormData(prev => ({ ...prev, battle_frequency: val }))}
                                onConfigure={() => setActiveTab('truth')}
                                // [FIX] Squad Persistence (No Surprises Protocol)
                                savedSquad={formData.source_of_truth?.squad_ids || []}
                                onSquadChange={(ids) => setFormData(prev => ({
                                    ...prev,
                                    source_of_truth: { ...(prev.source_of_truth || {}), squad_ids: ids }
                                }))}
                            />
                        </div>
                    )}

                    {activeTab === 'analytics' && (
                        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
                            <AnalyticsView profile={profile} />
                        </div>
                    )}

                    {activeTab === 'warfare' && (
                        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', minHeight: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <ContentWarfarePanel
                                profile={profile} // [FIX] Pass full object to unlock UI
                                savedFixes={savedFixes}
                                onRefresh={() => fetchSavedFixes(profile?.id)}
                                onGenerateFix={handleGenerateFix}
                                onGenerateSchema={handleGenerateSchema}
                                onPostToWP={handlePostToWP}
                                generatingArticle={false}
                                generatingSchema={false}
                            />

                            {/* ACTIVE ASSETS LIST */}
                            <div className="ui-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                <div className="ui-card-header" style={{ color: '#0f172a' }}>
                                    <HistoryIcon size={16} className="text-slate-500" /> ACTIVE ASSETS & HISTORY
                                </div>
                                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                    <AssetHistoryList
                                        fixes={savedFixes}
                                        assets={savedFixes}
                                        onPreview={setPreviewContent}
                                        onPostToWP={handlePostToWP}
                                        onPost={handlePostToWP}
                                        onCopy={(c) => { navigator.clipboard.writeText(c); showToast("Copied!", 'success'); }}
                                    />
                                </div>
                            </div>

                            {/* CONTENT FACTORY STRATEGY (Moved here per user request) */}
                            <ContentStrategyPanel
                                locationId={profile?.id}
                                initialSettings={{
                                    daily_limit: formData.ai_daily_limit || 3,
                                    active: formData.auto_poster_enabled || false,
                                    posts_per_hour: formData.ai_posting_speed === 'turbo' ? 5 : 1, // Simple mapping
                                    social_youtube: profile.social_youtube || '',

                                    // Automation
                                    auto_battle_enabled: profile.auto_battle_enabled || false,
                                    ai_provider: profile.ai_provider || 'openai',
                                    battle_frequency: profile.battle_frequency || '24h',
                                    active_workers: profile.source_of_truth?.active_workers || { gemini: true, gpt: false, grok: false, deepseek: false },

                                    // Keys
                                    openai_api_key: '',
                                    gemini_api_key: '',
                                }}
                                onSave={async (settings) => {
                                    // [FIX] Map Panel keys to Profile keys
                                    const mappedSettings = {
                                        ai_daily_limit: settings.daily_limit,
                                        auto_poster_enabled: settings.active,
                                        // [FIX] Map Speed
                                        ai_posting_speed: settings.posts_per_hour === 5 ? 'turbo' : 'standard',
                                        // [FIX] New Strategy Keys
                                        ai_power: settings.ai_power,
                                        cornerstone_first: settings.cornerstone_first
                                    };

                                    const updatedData = { ...formData, ...mappedSettings };
                                    setFormData(updatedData);

                                    try {
                                        // [FIX] Direct API Save for Strategy Panel
                                        if (profile?.id) {
                                            await reputationService.updateProfile(profile.id, updatedData);
                                            // Update initialData to sync "Clean" state for Smart Save button
                                            setInitialData(updatedData);
                                        }
                                        if (onSave) {
                                            await onSave(profile?.id, updatedData);
                                        }
                                        showToast("Strategy Configuration Saved", 'success');
                                    } catch (e) {
                                        showToast("Save Failed: " + e.message, 'error');
                                    }
                                }}
                            />
                        </div>
                    )}

                    {activeTab === 'feed' && (
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <SmartReputationFeed
                                battles={sessions}
                                height="100%"
                                defaultLocation={formData.location_name} // [FIX] Context Injection
                                onFixRequired={(id, loc, kw) => {
                                    handleFixFromFeed({ id, location: loc, keyword: kw });
                                }}
                                onInspect={(battle) => setInspectingBattle(battle)}
                            />
                        </div>
                    )}

                    {/* BATTLE ANALYSIS OVERLAY */}
                    {inspectingBattle && (
                        <BattleAnalysisView
                            battle={inspectingBattle}
                            onClose={() => setInspectingBattle(null)}
                            onCreateContent={(strategy) => {
                                setInspectingBattle(null);
                                setActiveTab('warfare');
                                setFormData(prev => ({ ...prev, custom_prompt: strategy }));
                                showToast("Strategy Loaded. Ready to Fire.", 'success');
                            }}
                        />
                    )}

                </div>
            </div>
        </div>
    );
};

export default ReputationProfileEditor;